import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Button,
    useTheme,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { departmentService, Department } from '../../services/department-service';
import { purposeService } from '../../services/purpose-service';
import { config } from '../../config/config';

interface AddPurposePanelProps {
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
}

// Initial form state
const initialFormState = {
    name: '',
    departmentId: 0,
    description: ''
};

const initialErrorsState = {
    name: '',
    department: ''
};

const AddPurposePanel: React.FC<AddPurposePanelProps> = ({ open, onClose, onSave }) => {
    const theme = useTheme();
    
    // Form state
    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState(initialErrorsState);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [loading, setLoading] = useState(false);
    const [departmentsLoading, setDepartmentsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Reset form when panel opens
    useEffect(() => {
        if (open) {
            setFormData(initialFormState);
            setErrors(initialErrorsState);
        }
    }, [open]);

    // Load departments
    useEffect(() => {
        if (open) {
            const loadDepartments = async () => {
                try {
                    setDepartmentsLoading(true);
                    const depts = await departmentService.getDepartments();
                    setDepartments(depts);
                } catch (error) {
                    console.error('Error loading departments:', error);
                } finally {
                    setDepartmentsLoading(false);
                }
            };
            loadDepartments();
        }
    }, [open]);

    // Check if form has any data
    const hasFormData = formData.name.trim() !== '' || formData.departmentId !== 0 || formData.description.trim() !== '';

    // Check if form is valid
    const isFormValid = formData.name.trim().length >= 3 && formData.departmentId !== 0 && !errors.name && !errors.department;

    const validateName = (name: string): boolean => {
        if (name.trim().length < 3) {
            setErrors(prev => ({
                ...prev,
                name: 'Name must be at least 3 characters long'
            }));
            return false;
        }
        setErrors(prev => ({
            ...prev,
            name: ''
        }));
        return true;
    };

    const validateDepartment = (departmentId: number): boolean => {
        if (!departmentId) {
            setErrors(prev => ({
                ...prev,
                department: 'Department is required'
            }));
            return false;
        }
        setErrors(prev => ({
            ...prev,
            department: ''
        }));
        return true;
    };

    const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (field === 'name') {
            validateName(value);
        }
    };

    const handleDepartmentChange = (event: SelectChangeEvent<number>) => {
        const departmentId = event.target.value as number;
        setFormData(prev => ({
            ...prev,
            departmentId
        }));
        validateDepartment(departmentId);
    };

    const handleSave = async () => {
        if (!isFormValid) return;

        try {
            setLoading(true);
            setError(null);
            
            // Call the service to create the purpose
            await purposeService.createPurpose({
                departmentId: formData.departmentId,
                name: formData.name.trim(),
                description: formData.description.trim()
            });
            
            // Reset form after successful save
            setFormData(initialFormState);
            setErrors(initialErrorsState);
            onClose();
            onSave?.(); // Refresh the grid
        } catch (error: any) {
            console.error('Error saving purpose:', error);
            setError(error.response?.data?.message || 'Failed to create purpose');
            // Keep panel open on error
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (hasFormData) {
            // TODO: Show confirmation dialog if form has data
            console.log('Form has data, should show confirmation');
        }
        setFormData(initialFormState);
        setErrors(initialErrorsState);
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '600px',
                    bgcolor: theme.palette.background.paper,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Add Purpose
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Form Content */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <Box sx={{ p: 3 }}>
                        <Stack spacing={3}>
                            <TextField
                                label="Name"
                                fullWidth
                                value={formData.name}
                                onChange={handleChange('name')}
                                error={Boolean(errors.name)}
                                helperText={errors.name}
                                required
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme.palette.divider,
                                        },
                                    },
                                }}
                            />

                            <FormControl 
                                fullWidth 
                                error={Boolean(errors.department)}
                                required
                                disabled={loading || departmentsLoading}
                            >
                                <InputLabel id="department-label">Department</InputLabel>
                                <Select
                                    labelId="department-label"
                                    value={formData.departmentId}
                                    onChange={handleDepartmentChange}
                                    label="Department"
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.divider,
                                        },
                                    }}
                                >
                                    {departmentsLoading ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Loading departments...
                                        </MenuItem>
                                    ) : (
                                        departments.map((dept) => (
                                            <MenuItem 
                                                key={dept.departmentId} 
                                                value={dept.departmentId}
                                                sx={{ color: theme.palette.text.primary }}
                                            >
                                                {dept.departmentName}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                                {errors.department && (
                                    <Typography 
                                        color="error" 
                                        variant="caption" 
                                        sx={{ mt: 0.5, ml: 2 }}
                                    >
                                        {errors.department}
                                    </Typography>
                                )}
                            </FormControl>

                            <TextField
                                label="Description"
                                fullWidth
                                value={formData.description}
                                onChange={handleChange('description')}
                                multiline
                                rows={3}
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme.palette.divider,
                                        },
                                    },
                                }}
                            />
                        </Stack>
                    </Box>
                </Box>

                {/* Footer with buttons */}
                <Box sx={{ 
                    p: 2, 
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        gap: 2 
                    }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleCancel}
                            sx={{
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    borderColor: theme.palette.primary.dark,
                                    bgcolor: theme.palette.action.hover,
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleSave}
                            disabled={!isFormValid || loading}
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.dark,
                                },
                                '&.Mui-disabled': {
                                    bgcolor: theme.palette.action.disabledBackground,
                                    color: theme.palette.action.disabled,
                                }
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                                    Saving...
                                </>
                            ) : (
                                'Save'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Error Snackbar */}
            <Snackbar 
                open={!!error} 
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                onClose={() => setError(null)}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Drawer>
    );
};

export default AddPurposePanel; 